import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import type { WhyUsItem } from '../types';

interface Props {
  items: WhyUsItem[];
}

function WhyCard({ icon, title, desc, index }: WhyUsItem & { index: number }) {
  const { ref, isVisible } = useScrollAnimation(0.1);

  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className={`relative group transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      style={{ transitionDelay: `${index * 120}ms` }}
    >
      <div className="bg-[#0D1E35] border border-[#C8A96A]/10 hover:border-[#C8A96A]/35 rounded-2xl p-6 h-full transition-all duration-400"
        style={{ borderTop: '3px solid rgba(200,169,106,0.5)' }}
      >
        <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-[#C8A96A]/20 to-[#A8894F]/10 rounded-xl mb-4 group-hover:from-[#C8A96A]/35 transition-all duration-300">
          <i className={`${icon} text-[#C8A96A] text-xl`} />
        </div>
        <h3 className="font-playfair text-white font-bold text-base mb-2">{title}</h3>
        <p className="text-white/55 text-sm leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}

export default function ServiceWhyUs({ items }: Props) {
  const { ref: headRef, isVisible: headVisible } = useScrollAnimation();

  return (
    <section className="py-20 bg-[#0A1628] relative overflow-hidden">
      <div
        className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-8 blur-3xl pointer-events-none"
        style={{ background: 'radial-gradient(circle, #C8A96A, transparent)' }}
      />

      <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10">
        <div
          ref={headRef as React.RefObject<HTMLDivElement>}
          className={`text-center mb-12 transition-all duration-700 ${headVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <p className="text-[#C8A96A] text-xs tracking-widest uppercase font-medium mb-3">Why Choose Us</p>
          <h2 className="font-playfair text-3xl md:text-5xl font-bold text-white leading-tight">
            Sleaford&apos;s{' '}
            <span className="bg-gradient-to-r from-[#C8A96A] to-[#E0C78A] bg-clip-text text-transparent">
              Trusted Electrician
            </span>
          </h2>
          <p className="text-white/50 mt-3 max-w-xl mx-auto text-sm md:text-base">
            NAPIT registered, fully insured, and proud to serve Sleaford and Lincolnshire with honest, quality electrical work.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {items.map((item, i) => (
            <WhyCard key={item.title} {...item} index={i} />
          ))}
        </div>

        {/* Trust strip */}
        <div className="mt-14 border border-[#C8A96A]/15 rounded-2xl p-6 bg-[#071120]/60">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { icon: 'ri-shield-check-fill', label: 'NAPIT', sublabel: 'Registered' },
              { icon: 'ri-award-fill', label: 'Part P', sublabel: 'Certified' },
              { icon: 'ri-time-fill', label: 'Emergency', sublabel: 'Call-Out' },
              { icon: 'ri-thumb-up-fill', label: '5 Star', sublabel: 'Google Reviews' },
            ].map(({ icon, label, sublabel }) => (
              <div key={label} className="flex flex-col items-center gap-1.5">
                <div className="w-11 h-11 flex items-center justify-center rounded-full border border-[#C8A96A]/30 bg-[#C8A96A]/6">
                  <i className={`${icon} text-[#C8A96A] text-base`} />
                </div>
                <p className="text-white font-semibold text-sm">{label}</p>
                <p className="text-white/40 text-xs">{sublabel}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
